import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { IAuthClient, AuthStatus, authClient as defaultAuthClient } from '../services/authClient';
import { getMicrosoftProfile } from '../services/apiClient';

/**
 * AuthContext - Global authentication state management
 *
 * Provides authentication status and operations throughout the app.
 * Supports injectable authClient for testing/mocking.
 */

interface AuthContextValue {
  /** Current authentication status */
  authStatus: AuthStatus | null;
  /** Whether initial auth check is in progress */
  isLoading: boolean;
  /** Begin Microsoft OAuth login flow */
  login: () => Promise<void>;
  /** Logout current user */
  logout: (localOnly?: boolean) => Promise<void>;
  /** Error message from last auth operation */
  error: string | null;
  /** Loading state for login operation */
  isLoggingIn: boolean;
  /** Clear error message */
  clearError: () => void;
  /** Reason code for last error (e.g., 'CONSENT_REQUIRED') */
  errorReason: string | null;
  /** Admin consent URL (if consent is required) */
  adminConsentUrl: string | null;
  /** Tenant ID (if available from error) */
  tenantId: string | null;
  /** Refresh user profile from backend */
  refreshUser: () => Promise<void>;
  /** Microsoft Graph displayName */
  microsoftDisplayName: string | null;
  /** Microsoft Graph profile photo as data URL */
  microsoftPhotoUrl: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  /** Optional auth client for testing/mocking */
  authClient?: IAuthClient;
}

export function AuthProvider({ children, authClient = defaultAuthClient }: AuthProviderProps) {
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorReason, setErrorReason] = useState<string | null>(null);
  const [adminConsentUrl, setAdminConsentUrl] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [microsoftDisplayName, setMicrosoftDisplayName] = useState<string | null>(null);
  const [microsoftPhotoUrl, setMicrosoftPhotoUrl] = useState<string | null>(null);

  // Initial auth check on mount
  useEffect(() => {
    let mounted = true;

    async function checkAuth() {
      try {
        const status = await authClient.getStatus();
        if (mounted) {
          setAuthStatus(status);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to check auth status:', err);
        if (mounted) {
          setAuthStatus({ authenticated: false });
          setIsLoading(false);
        }
      }
    }

    checkAuth();

    return () => {
      mounted = false;
    };
  }, [authClient]);

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = authClient.onAuthChanged((newStatus) => {
      setAuthStatus(newStatus);
    });

    return unsubscribe;
  }, [authClient]);

  // Fetch Microsoft profile (displayName + photo) when authenticated
  useEffect(() => {
    if (!authStatus?.authenticated || !authStatus.user) {
      setMicrosoftDisplayName(null);
      setMicrosoftPhotoUrl(null);
      return;
    }

    let mounted = true;

    async function fetchMicrosoftProfile() {
      try {
        const response = await getMicrosoftProfile();
        if (mounted && response.success && response.data) {
          setMicrosoftDisplayName(response.data.displayName);
          setMicrosoftPhotoUrl(response.data.photoDataUrl);
        }
      } catch (err) {
        console.error('[AuthContext] Failed to fetch Microsoft profile:', err);
      }
    }

    fetchMicrosoftProfile();

    return () => {
      mounted = false;
    };
  }, [authStatus?.authenticated, authStatus?.user?.email]);

  const login = async () => {
    setIsLoggingIn(true);
    setError(null);
    setErrorReason(null);
    setAdminConsentUrl(null);
    setTenantId(null);

    try {
      const result = await authClient.beginLogin();

      if (result.success && result.status) {
        setAuthStatus(result.status);
      } else {
        setError(result.error || 'Login failed');
        setErrorReason(result.reason || null);
        setAdminConsentUrl(result.adminConsentUrl || null);
        setTenantId(result.tenantId || null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('Login error:', err);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const logout = async (localOnly?: boolean) => {
    try {
      await authClient.logout(localOnly);
      setAuthStatus({ authenticated: false });
      setError(null);
      setErrorReason(null);
      setAdminConsentUrl(null);
      setTenantId(null);
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to logout');
    }
  };

  const clearError = () => {
    setError(null);
    setErrorReason(null);
    setAdminConsentUrl(null);
    setTenantId(null);
  };

  const refreshUser = async () => {
    try {
      const status = await authClient.getStatus();
      setAuthStatus(status);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const value: AuthContextValue = {
    authStatus,
    isLoading,
    login,
    logout,
    error,
    isLoggingIn,
    clearError,
    errorReason,
    adminConsentUrl,
    tenantId,
    refreshUser,
    microsoftDisplayName,
    microsoftPhotoUrl
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

/**
 * Hook to get authentication status (shorthand)
 * @returns authenticated boolean or null if loading
 */
export function useIsAuthenticated(): boolean | null {
  const { authStatus, isLoading } = useAuth();

  if (isLoading) return null;
  return authStatus?.authenticated ?? false;
}
